import { useAppTheme } from '@droplet-tech-code/core-elements/module/theme';
import { DelayRender } from '@droplet-tech-code/core-elements/module/ui/Delay';
import { FormHandler } from '@droplet-tech-code/core-elements/module/ui/Inputs/Form';
import { VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { ScrollView } from '@droplet-tech-code/core-elements/module/ui/View';
import React, { useRef } from 'react';

import { BookingInfo } from '~/components/Bookings/BookingInfo';
import { PageContainer } from '~/components/Page/Page.Container';
import { SupportConversationInputPanel } from '~/components/SupportConversation/InputPanel/Conversation.InputPanel';
import { chatCache } from '~/components/SupportConversation/SupportConversation.utils';
import { SupportRequestConversation } from '~/components/SupportConversation/SupportRequestConversation';
import { SupportRequestAccordionComponent } from '~/components/SupportRequest/SupportRequestAccordion';
import { useAttachParamsToUrl } from '~/hooks/nav.hook';
import {
  RootStackParamList,
  ScreenPropSupportRequestSingle,
} from '~/navigation/Navigation.types';

import { SupportRequestItemHeader } from './SupportRequest.ItemHeader';
import {
  AutoAssignedCard,
  BasketErrorInfo,
  CfarCard,
  KoalaCard,
  PaymentSummary,
  SupportRequestPurchasesComponent,
} from './SupportRequestItem';
import { supportRequestApi } from './SupportRequests.api';
import { SwitchStack } from '~/components/Stack/SwitchStack';

export const SupportRequestSingleScreen = (props: ScreenPropSupportRequestSingle) => {
  return <SupportRequestSingle supportId={props.route.params.id} />;
};

export const SupportRequestSingle = ({
  supportId,
  onClose,
}: {
  originScreen?: keyof RootStackParamList;
  supportId: string;
  onClose?: () => void;
}) => {
  useAttachParamsToUrl('SupportRequestSingle', { id: supportId });

  const { data, isLoading } = supportRequestApi.useGetSupportRequestQuery(
    supportId ?? '',
    {
      skip: !supportId,
      refetchOnMountOrArgChange: !!supportId,
    },
  );

  const { spacing } = useAppTheme();
  const advancedRef = useRef(new FormHandler());
  const textRef = useRef(new FormHandler());

  const cachedInitText = chatCache.cache[supportId];

  if (data === undefined) {
    return null;
  }

  const check = data.check;
  const bookingId = data.supportRequest.bookingId;
  const currentPurchaseId = data.supportRequest.purchaseId;
  const conversation = data.supportRequest.conversation;
  const basketError = data.supportRequest.basketError;
  const hasZdData = !!(data.supportRequest as any).zdData;

  return (
    <DelayRender isLoading={isLoading}>
      <SupportRequestItemHeader
        supportRequest={data.supportRequest}
        check={check}
        onClose={onClose}
      />
      <PageContainer bg="monochrome-base">
        <SwitchStack
          flex={1}
          space="2.5"
          style={{ alignSelf: 'stretch', alignItems: 'flex-start' }}
        >
          <VStack style={{ height: '100%' }}>
            <ScrollView
              contentContainerStyle={{
                flexGrow: 1,
                paddingHorizontal: spacing[1],
              }}
            >
              <VStack space="2.5" py="2.5">
                {basketError ? <BasketErrorInfo basketError={basketError} /> : null}
                <BookingInfo
                  bookingId={bookingId}
                  supportId={supportId}
                  isPrioritySupport={
                    (data.supportRequest as { isPrioritySupport?: boolean })
                      .isPrioritySupport
                  }
                />
                <PaymentSummary bookingId={bookingId} />
                <AutoAssignedCard supportRequest={data.supportRequest} check={check} />
                <KoalaCard bookingId={data.supportRequest.bookingId} />
                <CfarCard bookingId={data.supportRequest.bookingId} />
                <SupportRequestPurchasesComponent
                  currentPurchaseId={currentPurchaseId}
                  bookingId={bookingId}
                  supportId={supportId}
                />
              </VStack>
            </ScrollView>
          </VStack>
          {!hasZdData ? (
            <VStack
              flex={1}
              py="2.5"
              style={[
                { height: '100%', alignItems: 'center' },
                {
                  minWidth: 700,
                },
              ]}
              space="2.5"
            >
              <SupportRequestConversation
                supportRequestId={supportId}
                check={check}
                supportConversation={conversation}
              />
              <SupportConversationInputPanel
                supportId={supportId}
                initialText={cachedInitText}
                key={supportId}
                supportRequestRes={data}
                advancedRef={advancedRef}
                textRef={textRef}
              />
            </VStack>
          ) : null}
          <VStack
            style={{ width: 400, height: '100%', borderLeftWidth: 1 }}
            bc="monochrome-extraLight"
          >
            <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
              <SupportRequestAccordionComponent data={data} />
            </ScrollView>
          </VStack>
        </SwitchStack>
      </PageContainer>
    </DelayRender>
  );
};
